import { CommonModule } from '@angular/common';
import {
  Component, EventEmitter, HostListener, Input, Output, OnChanges, SimpleChanges,
} from '@angular/core';
import { LightboxMedia } from './lightbox.model';

/**
 * Visionneuse plein écran, reprise de celle de la page Galerie et rendue
 * réutilisable pour les articles et les sections de page.
 *
 * Le composant ne connaît que sa liste de médias et l'index courant : c'est
 * la page hôte qui décide quand l'ouvrir. La navigation au clavier
 * (flèches, Échap) est prise en charge ici.
 */
@Component({
  selector: 'app-lightbox',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './lightbox.component.html',
  styleUrl: './lightbox.component.css',
})
export class LightboxComponent implements OnChanges {
  /** Médias parcourables. */
  @Input() media: LightboxMedia[] = [];
  /** Index affiché. */
  @Input() index = 0;
  /** Ouverture pilotée par la page hôte. */
  @Input() open = false;

  @Output() closed = new EventEmitter<void>();
  @Output() indexChange = new EventEmitter<number>();

  ngOnChanges(changes: SimpleChanges): void {
    // Le défilement de la page est bloqué tant que la visionneuse est
    // ouverte : sans cela, la molette fait défiler l'arrière-plan.
    if (changes['open']) {
      document.body.style.overflow = this.open ? 'hidden' : '';
    }
  }

  get current(): LightboxMedia | null {
    return this.media[this.index] ?? null;
  }

  get total(): number {
    return this.media.length;
  }

  /** Vrai si le média courant est une vidéo, quel que soit le format reçu. */
  isVideo(m: LightboxMedia | null): boolean {
    if (!m) return false;
    if (m.type) return m.type === 'video';
    if (m.mime_type) return m.mime_type.startsWith('video/');
    return /\.(mp4|webm|mov|ogg)$/i.test(m.url ?? '');
  }

  close(): void {
    this.open = false;
    document.body.style.overflow = '';
    this.closed.emit();
  }

  prev(event?: Event): void {
    event?.stopPropagation();
    if (!this.total) return;
    this.index = (this.index - 1 + this.total) % this.total;
    this.indexChange.emit(this.index);
  }

  next(event?: Event): void {
    event?.stopPropagation();
    if (!this.total) return;
    this.index = (this.index + 1) % this.total;
    this.indexChange.emit(this.index);
  }

  stop(event: Event): void {
    event.stopPropagation();
  }

  @HostListener('document:keydown', ['$event'])
  onKey(event: KeyboardEvent): void {
    if (!this.open) return;

    switch (event.key) {
      case 'Escape':     this.close(); break;
      case 'ArrowLeft':  this.prev();  break;
      case 'ArrowRight': this.next();  break;
      default: return;
    }

    event.preventDefault();
  }
}
